(function () {
  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(window.Shopify && Shopify.locale ? Shopify.locale : "en-US", {
      style: "currency",
      currency: window.Shopify && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : "USD"
    }).format(value);
  };

  const getVariantPlan = (variant, planId) => {
    if (!variant || !Array.isArray(variant.sellingPlans)) return null;
    return variant.sellingPlans.find((plan) => String(plan.id) === String(planId)) || null;
  };

  const getFriendlyPlanName = (planOrName) => {
    const plan = typeof planOrName === "object" && planOrName !== null ? planOrName : null;
    const optionText = plan && Array.isArray(plan.options)
      ? plan.options.map((option) => option && (option.value || option.name || option)).join(" ")
      : "";
    const text = `${optionText} ${String(plan ? plan.name : planOrName || "").trim()}`.trim();
    const lower = text.toLowerCase();
    const daysMatch = lower.match(/(?:every\s+)?(\d+)\s*days?/);
    const weeksMatch = lower.match(/(?:every\s+)?(\d+)\s*weeks?/);
    const monthsMatch = lower.match(/(?:every\s+)?(\d+)\s*months?/);

    if (daysMatch) {
      const days = Number(daysMatch[1]);
      if (days % 30 === 0) {
        const months = days / 30;
        return `Every ${months} ${months === 1 ? "Month" : "Months"}`;
      }
      if (days % 7 === 0) {
        const weeks = days / 7;
        return `Every ${weeks} ${weeks === 1 ? "Week" : "Weeks"}`;
      }
      return `Every ${days} Days`;
    }

    if (weeksMatch) {
      const weeks = Number(weeksMatch[1]);
      return `Every ${weeks} ${weeks === 1 ? "Week" : "Weeks"}`;
    }

    if (monthsMatch) {
      const months = Number(monthsMatch[1]);
      return `Every ${months} ${months === 1 ? "Month" : "Months"}`;
    }

    return text.replace(/^Billing And Delivery\s+/i, "");
  };

  const getPlanIntervalName = (planOrName) => {
    return getFriendlyPlanName(planOrName).replace(/^Every\s+/i, "");
  };

  const formatShortDate = (date = new Date()) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getPackSavingsPercent = (variants, variant) => {
    const baseVariant = variants.find((item) => Number(item.quantity || 1) === 1) || variants[0];
    const baseQuantity = Math.max(1, Number(baseVariant.quantity || 1));
    const currentQuantity = Math.max(1, Number(variant.quantity || 1));
    const basePerFilter = Number(baseVariant.price || 0) / baseQuantity;
    const currentPerFilter = Number(variant.price || 0) / currentQuantity;

    if (!basePerFilter || currentPerFilter >= basePerFilter) return 0;
    return Math.round(((basePerFilter - currentPerFilter) / basePerFilter) * 100);
  };

  const getBasePackTotal = (variants, variant) => {
    const baseVariant = variants.find((item) => Number(item.quantity || 1) === 1) || variants[0];
    const baseQuantity = Math.max(1, Number(baseVariant.quantity || 1));
    const currentQuantity = Math.max(1, Number(variant.quantity || 1));
    const basePerFilter = Number(baseVariant.price || 0) / baseQuantity;

    return Math.round(basePerFilter * currentQuantity);
  };

  const getPlanSavingsPercent = (variant, plan) => {
    if (!variant || !plan || !variant.price) return 0;
    return Math.round((Math.max(0, Number(variant.price) - Number(plan.price || 0)) / Number(variant.price)) * 100);
  };

  const getBestPlan = (variant) => {
    if (!variant || !Array.isArray(variant.sellingPlans) || !variant.sellingPlans.length) return null;

    return variant.sellingPlans.reduce((best, plan) => {
      if (!best) return plan;
      return getPlanSavingsPercent(variant, plan) > getPlanSavingsPercent(variant, best) ? plan : best;
    }, null);
  };

  const getSavingsBreakdown = (product, variant, plan, mode) => {
    const quantity = Math.max(1, Number(variant.quantity || 1));
    const price = mode === "subscription" && plan ? Number(plan.price || 0) : Number(variant.price || 0);
    const basePackTotal = getBasePackTotal(product.variants, variant);
    const multipackSavings = Math.max(0, basePackTotal - Number(variant.price || 0));
    const subscriberSavings = mode === "subscription" && plan ? Math.max(0, Number(variant.price || 0) - price) : 0;
    const totalSavings = Math.max(0, multipackSavings + subscriberSavings);
    const totalSavingsPercent = basePackTotal > 0 ? Math.round((totalSavings / basePackTotal) * 100) : 0;

    return {
      basePackTotal,
      multipackSavings,
      subscriberSavings,
      totalSavings,
      totalSavingsPercent,
      quantity,
      price,
      perFilter: Math.round(price / quantity)
    };
  };

  const getActivePricing = (state) => {
    const product = state.products[state.productIndex];
    const variant = product && product.variants[state.variantIndex];
    if (!variant) return null;

    const plan = state.mode === "subscription" ? getVariantPlan(variant, state.sellingPlanId) : null;
    const price = plan ? plan.price : variant.price;
    const compare = plan ? plan.compareAtPrice || variant.compareAtPrice || variant.price : variant.compareAtPrice || variant.price;
    const savings = Math.max(0, compare - price);

    return { product, variant, plan, price, compare, savings };
  };

  const setPressed = (buttons, activeButton) => {
    buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  };

  const initBuilders = (scope = document) => {
    scope.querySelectorAll("[data-sub-builder]").forEach((root) => {
    if (root.dataset.subBuilderInitialized === "true") return;
    root.dataset.subBuilderInitialized = "true";

    const dataScript = root.querySelector("[data-sub-products-json]");
    if (!dataScript) return;

    let products = [];
    try {
      products = JSON.parse(dataScript.textContent || "[]");
    } catch (error) {
      products = [];
    }

    if (!products.length) return;

    const state = {
      products,
      productIndex: 0,
      variantIndex: 0,
      mode: "subscription",
      sellingPlanId: null,
      usePurifierImage: false
    };

    const productButtons = Array.from(root.querySelectorAll("[data-sub-product-index]"));
    const productImages = Array.from(root.querySelectorAll("[data-sub-product-image]"));
    const purifierToggle = root.querySelector("[data-sub-purifier-toggle]");
    const variantWrap = root.querySelector("[data-sub-variant-options]");
    const frequencyWrap = root.querySelector("[data-sub-frequency-options]");
    const modeButtons = Array.from(root.querySelectorAll("[data-sub-mode]"));
    const priceEl = root.querySelector("[data-sub-price]");
    const perEl = root.querySelector("[data-sub-per]");
    const summaryProductEl = root.querySelector("[data-sub-summary-product]");
    const summaryCompareEl = root.querySelector("[data-sub-summary-compare]");
    const summaryDiscountEl = root.querySelector("[data-sub-summary-discount]");
    const summaryMultipackRow = root.querySelector("[data-sub-multipack-row]");
    const summaryMultipackDiscountEl = root.querySelector("[data-sub-summary-multipack-discount]");
    const summaryPerFilterEl = root.querySelector("[data-sub-summary-per-filter]");
    const summaryRewardsEl = root.querySelector("[data-sub-summary-rewards]");
    const saveBadgeEl = root.querySelector("[data-sub-save-badge]");
    const noteTitleEl = root.querySelector("[data-sub-note-title]");
    const noteBodyEl = root.querySelector("[data-sub-note-body]");
    const addButton = root.querySelector("[data-sub-add]");

    const renderProductImages = () => {
      productImages.forEach((img) => {
        const defaultSrc = img.getAttribute("data-default-src");
        const purifierSrc = img.getAttribute("data-purifier-src");
        img.src = state.usePurifierImage && purifierSrc ? purifierSrc : defaultSrc;
      });
      if (purifierToggle) {
        purifierToggle.textContent = state.usePurifierImage
          ? purifierToggle.getAttribute("data-default-label")
          : purifierToggle.getAttribute("data-purifier-label");
      }
    };

    const renderVariants = () => {
      const product = state.products[state.productIndex];
      variantWrap.innerHTML = "";
      product.variants.forEach((variant, index) => {
        const packSavingsPercent = getPackSavingsPercent(product.variants, variant);
        const button = document.createElement("button");
        button.type = "button";
        button.className = "sub-choice";
        button.dataset.subVariantIndex = String(index);
        button.setAttribute("aria-pressed", index === state.variantIndex ? "true" : "false");
        button.textContent = variant.title;
        if (index === state.variantIndex) button.classList.add("is-active");
        if (packSavingsPercent > 0 || variant.badge) {
          const badge = document.createElement("span");
          badge.className = "sub-choice__badge";
          badge.textContent = variant.badge || `SAVE ${packSavingsPercent}%`;
          button.appendChild(badge);
        }
        button.addEventListener("click", () => {
          state.variantIndex = index;
          chooseDefaultPlan();
          render();
        });
        variantWrap.appendChild(button);
      });
    };

    const chooseDefaultPlan = () => {
      const pricing = getActivePricing(state);
      const variant = pricing && pricing.variant;
      if (!variant || !variant.sellingPlans.length) {
        state.mode = "onetime";
        state.sellingPlanId = null;
        return;
      }
      if (state.mode === "subscription" && !variant.sellingPlans.some((plan) => String(plan.id) === String(state.sellingPlanId))) {
        const bestPlan = getBestPlan(variant);
        state.sellingPlanId = bestPlan ? bestPlan.id : variant.sellingPlans[0].id;
      }
    };

    const renderFrequencies = () => {
      const product = state.products[state.productIndex];
      const variant = product.variants[state.variantIndex];
      frequencyWrap.innerHTML = "";
      frequencyWrap.classList.toggle("is-hidden", state.mode !== "subscription");
      frequencyWrap.setAttribute("aria-hidden", state.mode === "subscription" ? "false" : "true");

      if (!variant.sellingPlans.length) {
        const empty = document.createElement("div");
        empty.className = "sub-empty";
        empty.textContent = root.getAttribute("data-no-selling-plans-text") || "No subscription plans are available for this variant.";
        frequencyWrap.appendChild(empty);
        return;
      }

      const plansWithSavings = variant.sellingPlans.map((plan) => ({
        ...plan,
        computedSavingsPercent: getPlanSavingsPercent(variant, plan)
      }));
      const maxSavingsPercent = Math.max(0, ...plansWithSavings.map((plan) => Number(plan.computedSavingsPercent || 0)));
      const bestPlan = plansWithSavings.find((plan) => Number(plan.computedSavingsPercent || 0) === maxSavingsPercent);
      plansWithSavings.forEach((plan) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "sub-frequency";
        button.dataset.subSellingPlan = String(plan.id);
        button.setAttribute("aria-pressed", String(plan.id) === String(state.sellingPlanId) ? "true" : "false");
        const label = document.createElement("span");
        label.className = "sub-frequency__label";
        label.textContent = plan.name;
        button.appendChild(label);
        if (String(plan.id) === String(state.sellingPlanId)) button.classList.add("is-active");
        const planSavingsPercent = Number(plan.computedSavingsPercent || 0);
        if (bestPlan && String(plan.id) === String(bestPlan.id) && planSavingsPercent > 0) {
          const badge = document.createElement("span");
          badge.className = "sub-frequency__badge";
          const badgeLabel = document.createElement("span");
          badgeLabel.className = "sub-frequency__badge-label";
          badgeLabel.textContent = "BEST SAVINGS";
          const badgeValue = document.createElement("span");
          badgeValue.className = "sub-frequency__badge-value";
          badgeValue.textContent = `-${planSavingsPercent}%`;
          badge.append(badgeLabel, badgeValue);
          button.appendChild(badge);
        }
        button.addEventListener("click", () => {
          state.mode = "subscription";
          state.sellingPlanId = plan.id;
          render();
        });
        frequencyWrap.appendChild(button);
      });
    };

    const renderSummary = () => {
      const pricing = getActivePricing(state);
      if (!pricing) return;

      const { product, variant, plan } = pricing;
      const savings = getSavingsBreakdown(product, variant, plan, state.mode);

      priceEl.textContent = formatMoney(savings.price);
      perEl.textContent = state.mode === "subscription" && plan ? `/ ${getPlanIntervalName(plan)}` : "";
      summaryProductEl.textContent = `${product.title} x ${savings.quantity}`;
      summaryCompareEl.textContent = formatMoney(savings.basePackTotal);
      if (summaryMultipackRow && summaryMultipackDiscountEl) {
        summaryMultipackRow.hidden = savings.multipackSavings <= 0;
        summaryMultipackDiscountEl.textContent = savings.multipackSavings > 0 ? `-${formatMoney(savings.multipackSavings)}` : formatMoney(0);
      }
      summaryDiscountEl.textContent = savings.subscriberSavings > 0 ? `-${formatMoney(savings.subscriberSavings)}` : formatMoney(0);
      summaryPerFilterEl.textContent = formatMoney(savings.perFilter);
      if (summaryRewardsEl) {
        const cashbackRate = state.mode === "subscription" && plan ? 0.05 : 0.03;
        summaryRewardsEl.textContent = formatMoney(Math.round(savings.price * cashbackRate));
      }
      const showSaveBadge = state.mode === "subscription" && savings.totalSavings > 0;
      saveBadgeEl.hidden = !showSaveBadge;
      saveBadgeEl.textContent = showSaveBadge ? `SAVE ${formatMoney(savings.totalSavings)}` : "";

      noteTitleEl.textContent = state.mode === "subscription" && plan
        ? `${product.title} Replacement Filter Subscription`
        : `${product.title} Replacement Filter`;
      if (state.mode === "subscription" && plan) {
        const recurringCompare = Math.max(Number(variant.price || 0), Number(plan.compareAtPrice || 0), Number(savings.price || 0));
        const recurringPrice = formatMoney(savings.price);
        const recurringCompareText = formatMoney(recurringCompare);
        noteBodyEl.innerHTML = `
          <div class="sub-plan-note__line">First order will process on <strong>${formatShortDate()}</strong></div>
          <div class="sub-plan-note__line">Delivered every <strong>${getPlanIntervalName(plan).toLowerCase()}</strong></div>
          <div class="sub-plan-note__line">Recurring payment: <span class="sub-plan-note__compare">${recurringCompareText}</span> <strong>${recurringPrice}</strong></div>
        `;
      } else {
        noteBodyEl.textContent = "This selection will be added as a one-time purchase.";
      }

      addButton.textContent = `${addButton.getAttribute("data-label")} - ${formatMoney(savings.price)}`;
      addButton.disabled = state.mode === "subscription" && !plan;
    };

    const render = () => {
      const product = state.products[state.productIndex];
      if (!product.variants[state.variantIndex]) state.variantIndex = 0;
      chooseDefaultPlan();
      setPressed(productButtons, productButtons[state.productIndex]);
      modeButtons.forEach((button) => {
        const active = button.dataset.subMode === state.mode;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });
      renderProductImages();
      renderVariants();
      renderFrequencies();
      renderSummary();
    };

    productButtons.forEach((button) => {
      button.addEventListener("click", () => {
        state.productIndex = Number(button.dataset.subProductIndex || 0);
        state.variantIndex = 0;
        state.sellingPlanId = null;
        chooseDefaultPlan();
        render();
      });
    });

    modeButtons.forEach((button) => {
      button.addEventListener("click", () => {
        state.mode = button.dataset.subMode;
        chooseDefaultPlan();
        render();
      });
    });

    if (purifierToggle) {
      purifierToggle.addEventListener("click", () => {
        state.usePurifierImage = !state.usePurifierImage;
        renderProductImages();
      });
    }

    if (addButton) {
      addButton.addEventListener("click", async () => {
        const pricing = getActivePricing(state);
        if (!pricing) return;

        const payload = {
          id: pricing.variant.id,
          quantity: 1
        };

        if (state.mode === "subscription" && pricing.plan) {
          payload.selling_plan = pricing.plan.id;
        }

        addButton.disabled = true;
        try {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest"
            },
            body: JSON.stringify({
              ...payload,
              sections: "cart-drawer,cart-icon-bubble"
            })
          });

          if (!response.ok) throw new Error("Unable to add subscription to cart.");
          const cartResponse = await response.json();
          const cartDrawer = document.querySelector("cart-drawer");

          if (cartDrawer && typeof cartDrawer.renderContents === "function" && typeof cartDrawer.open === "function") {
            cartDrawer.classList.remove("is-empty");
            cartDrawer.renderContents(cartResponse);
            cartDrawer.open();
            addButton.disabled = false;
            renderSummary();
          } else {
            window.location.href = "/cart";
          }
        } catch (error) {
          addButton.disabled = false;
          addButton.textContent = addButton.getAttribute("data-error-label") || "Try Again";
        }
      });
    }

    render();
  });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initBuilders());
  } else {
    initBuilders();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initBuilders(event.target);
  });
})();
